'use client'

import {Activity, useState, type ReactNode} from 'react'
import {usePathname} from 'next/navigation'
import {AlertTriangle} from 'lucide-react'
import {Button} from '@fitro/customer-ui/Button'
import {useTranslation} from '../../hooks/useTranslation'
import {useAuth, useClerk} from '@fitro/customer-host/auth'
import {useCustomerProfile} from '../../hooks/useCustomerProfile'
import {ApiError} from '../../services/api-helpers'
import {customerProfileApiAvailable} from '../../services/profile.service'
import {isPublicTenantEntryPath} from '../../lib/tenant-seo-policy'
import {HoldHydration} from '../shared/HoldHydration'

const BLOCKED_MESSAGE = 'Customer access to this studio is blocked'

export function isCustomerBlockedError(error: unknown): boolean {
    if (!(error instanceof ApiError) || error.status !== 403) return false

    const payload = error.data && typeof error.data === 'object'
        ? error.data as Record<string, unknown>
        : null

    return payload?.errorCode === 'CUSTOMER_BLOCKED'
        || payload?.message === BLOCKED_MESSAGE
        || error.message === BLOCKED_MESSAGE
}

function CustomerBlockedNotice() {
    const {t} = useTranslation('errors')
    const {signOut} = useClerk()
    const [signingOut, setSigningOut] = useState(false)

    const handleSignOut = () => {
        setSigningOut(true)
        void signOut({redirectUrl: '/sign-in'}).catch(() => setSigningOut(false))
    }

    return (
        <main className="min-h-svh w-full bg-slate-50 dark:bg-gray-950 flex items-center justify-center p-6">
            <div className="w-full max-w-xl rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 shadow-sm">
                <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-error-50 text-error-600">
                        <AlertTriangle className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs uppercase tracking-[0.25em] text-slate-400">
                            {t('customerBlocked.eyebrow')}
                        </p>
                        <h1 className="mt-2 text-2xl font-semibold text-slate-900 dark:text-gray-100">
                            {t('customerBlocked.title')}
                        </h1>
                        <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                            {t('customerBlocked.description')}
                        </p>
                    </div>
                </div>
                <div className="mt-6">
                    <Button
                        type="button"
                        variant="outline"
                        loading={signingOut}
                        onClick={handleSignOut}
                    >
                        {t('customerBlocked.signOut')}
                    </Button>
                </div>
            </div>
        </main>
    )
}

function CustomerAccessLoading() {
    return (
        <main className="min-h-svh w-full bg-white dark:bg-gray-950 flex items-center justify-center p-8" aria-busy="true">
            <div className="w-full max-w-md space-y-4">
                <div className="h-8 w-2/3 motion-safe:animate-pulse motion-reduce:animate-none rounded-lg bg-slate-100 dark:bg-gray-800" />
                <div className="h-4 w-full motion-safe:animate-pulse motion-reduce:animate-none rounded bg-slate-100 dark:bg-gray-800" />
                <div className="h-4 w-4/5 motion-safe:animate-pulse motion-reduce:animate-none rounded bg-slate-100 dark:bg-gray-800" />
            </div>
        </main>
    )
}

export function CustomerStudioAccessGuard({children}: {children: ReactNode}) {
    const {isLoaded, isSignedIn} = useAuth()
    const pathname = usePathname()
    const isPublicTenantPage = Boolean(pathname && isPublicTenantEntryPath(pathname))
    const profileQuery = useCustomerProfile({
        enabled: customerProfileApiAvailable && isLoaded && isSignedIn && !isPublicTenantPage,
    })

    if (!customerProfileApiAvailable) return children
    const checksAccess = isLoaded && isSignedIn && !isPublicTenantPage
    const isChecking = !isLoaded || (checksAccess && profileQuery.isLoading)
    const isBlocked = checksAccess && !profileQuery.isLoading && isCustomerBlockedError(profileQuery.error)
    const withheld = isChecking || isBlocked

    return <>
        {isChecking ? <CustomerAccessLoading /> : null}
        {isBlocked ? <CustomerBlockedNotice /> : null}
        <div style={{display: withheld ? 'none' : 'contents'}}>
            {/* The server cannot know the access state: it renders the page for validation. The browser renders it again
                inside the hidden Activity, which keeps its effects paused until access is confirmed. */}
            <HoldHydration release={!withheld}>
                <Activity mode={withheld && typeof window !== 'undefined' ? 'hidden' : 'visible'}>
                    {children}
                </Activity>
            </HoldHydration>
        </div>
    </>
}
